/**
 * The letter desk's data (WPS-231): how letters are filed by category and what
 * a sender has on the page. Kept apart from the components that render them.
 */
import { useMemo } from 'react'

import type { ComposerTemplate, ComposerTemplateGroup } from '@/admin/hooks/use-composer'

// The category key for letters that are part of this record's own workflow.
export const SUGGESTED_CATEGORY = '__suggested__'
// The category key for letters with no tags.
const UNFILED_CATEGORY = '__unfiled__'

export interface LetterCategory {
  key: string
  label: string
  items: ComposerTemplate[]
}

/** A letter's category: the first of its tags, the way its author filed it. */
function categoryOf(item: ComposerTemplate): string {
  const first = (item.tags ?? []).find((tag) => tag.trim() !== '')
  return first ? first.trim() : UNFILED_CATEGORY
}

export function dedupe(items: ComposerTemplate[]): ComposerTemplate[] {
  const seen = new Set<number>()
  return items.filter((item) => (seen.has(item.id) ? false : (seen.add(item.id), true)))
}

/**
 * Every letter once, filed by category, with the record's own letters first.
 * The groups the API serves are by workflow; a letter can sit in several.
 */
export function useLetterCategories(
  groups: ComposerTemplateGroup[],
  labels: { suggested: string; unfiled: string },
): LetterCategory[] {
  return useMemo(() => {
    const seen = new Map<number, ComposerTemplate>()
    const suggested: ComposerTemplate[] = []
    for (const group of groups) {
      for (const item of group.items) {
        if (!seen.has(item.id)) seen.set(item.id, item)
        if (group.is_current && !suggested.some((s) => s.id === item.id)) {
          suggested.push(item)
        }
      }
    }
    const byCategory = new Map<string, ComposerTemplate[]>()
    for (const item of seen.values()) {
      const key = categoryOf(item)
      byCategory.set(key, [...(byCategory.get(key) ?? []), item])
    }
    const byName = (a: ComposerTemplate, b: ComposerTemplate) => a.name.localeCompare(b.name)
    const filed = [...byCategory.entries()]
      .filter(([key]) => key !== UNFILED_CATEGORY)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, items]) => ({ key, label: key, items: items.sort(byName) }))
    const unfiled = byCategory.get(UNFILED_CATEGORY) ?? []
    return [
      ...(suggested.length
        ? [{ key: SUGGESTED_CATEGORY, label: labels.suggested, items: suggested.sort(byName) }]
        : []),
      ...filed,
      ...(unfiled.length
        ? [{ key: UNFILED_CATEGORY, label: labels.unfiled, items: unfiled.sort(byName) }]
        : []),
    ]
  }, [groups, labels.suggested, labels.unfiled])
}

/** What a sender has on the page: the header typed over, and the body. */
export interface LetterPageValue {
  date: string
  name: string
  address: string
  reference: string
  body: string
  /** Which parts the sender has typed into; untouched ones follow the record. */
  touched: { name: boolean; address: boolean; body: boolean }
}

export function asLetterPage(value: unknown): LetterPageValue {
  const v = (value ?? {}) as Partial<LetterPageValue>
  return {
    date: typeof v.date === 'string' ? v.date : '',
    name: typeof v.name === 'string' ? v.name : '',
    address: typeof v.address === 'string' ? v.address : '',
    reference: typeof v.reference === 'string' ? v.reference : '',
    body: typeof v.body === 'string' ? v.body : '',
    touched: {
      name: v.touched?.name === true,
      address: v.touched?.address === true,
      body: v.touched?.body === true,
    },
  }
}


/**
 * An email's filled-in body, ready to type into. Email layouts are tables,
 * which a typing editor would run into one line ("Claim Number CLM-1 Status
 * Open ..."); each two-cell row becomes "Label: value" on its own line and any
 * other cell its own block, innermost tables first. The layout's own inline
 * colours, sizes and fonts go too, so the text reads evenly while it is typed
 * into; links keep their address.
 */
export function tidyForEditing(html: string): string {
  if (!html || typeof DOMParser === 'undefined') return html
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html')
  for (const element of Array.from(doc.querySelectorAll('[style], [class]'))) {
    element.removeAttribute('style')
    element.removeAttribute('class')
  }
  const tables = Array.from(doc.querySelectorAll('table')).reverse()
  for (const table of tables) {
    const blocks: HTMLElement[] = []
    for (const row of Array.from(table.querySelectorAll(':scope > tbody > tr, :scope > tr'))) {
      const cells = Array.from(row.children).filter((cell) => /^(TD|TH)$/.test(cell.tagName))
      const plain = cells.every((cell) => !cell.querySelector('p, div, a, ul, ol, table, img'))
      if (cells.length === 2 && plain && cells[0].textContent?.trim()) {
        const line = doc.createElement('p')
        line.append(`${cells[0].textContent.trim()}: `)
        const value = doc.createElement('strong')
        value.textContent = cells[1].textContent?.trim() ?? ''
        line.append(value)
        blocks.push(line)
        continue
      }
      for (const cell of cells) {
        if (!cell.innerHTML.trim()) continue
        const block = doc.createElement('div')
        block.innerHTML = cell.innerHTML
        blocks.push(block)
      }
    }
    table.replaceWith(...blocks)
  }
  return doc.getElementById('root')?.innerHTML ?? html
}
